import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Search, Folder, MoreVertical } from 'lucide-react';
import BottomNavigation from '../../components/common/BottomNavigation';
import { useApp } from '../../context/AppContext';
import { useData } from '../../context/DataContext';
import { Category } from '../../types';

const AdminCategoryList: React.FC = () => {
  const { setCurrentScreen } = useApp();
  const { categories } = useData();
  const [searchTerm, setSearchTerm] = useState('');

  // Filter categories
  const filteredCategories = useMemo(() => {
    return categories.filter(category =>
      category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      category.description.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [categories, searchTerm]);

  const handleBack = () => {
    setCurrentScreen('admin-dashboard');
  };

  const handleManageCategories = () => {
    setCurrentScreen('admin-category');
  };


  const CategoryListItem: React.FC<{ category: Category; index: number }> = ({ category, index }) => (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.05 }}
      className="flex items-center justify-between p-4 bg-slate-800 rounded-lg border border-slate-700 hover:bg-slate-750 transition-colors duration-200"
    >
      <div className="flex items-center space-x-4">
        <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center">
          <span className="text-xl">{category.icon}</span>
        </div>
        <div>
          <h3 className="text-white font-medium text-lg">{category.name}</h3>
          <p className="text-slate-400 text-sm">{category.description}</p>
        </div>
      </div>
      <button className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors duration-200">
        <MoreVertical size={16} />
      </button>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-slate-900 pb-20">
      {/* Header */}
      <div className="flex items-center justify-between p-6 bg-slate-800 border-b border-slate-700">
        <div className="flex items-center space-x-4">
          <motion.button
            onClick={handleBack}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors duration-200"
          >
            <ArrowLeft size={20} />
          </motion.button>
          <div>
            <h1 className="text-xl font-bold text-white">Categories</h1>
            <p className="text-slate-400 text-sm">{categories.length} total categories</p>
          </div>
        </div>
        <motion.button
          onClick={handleManageCategories}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="flex items-center space-x-2 bg-teal-500 hover:bg-teal-600 text-white px-4 py-2 rounded-lg transition-colors duration-200"
        >
          <Folder size={16} />
          <span>Manage</span>
        </motion.button>
      </div>

      <div className="p-6 space-y-6">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
          <input
            type="text"
            placeholder="Search categories by name or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
          />
        </div>

        {/* Categories List */}
        <div className="space-y-3">
          {filteredCategories.length > 0 ? (
            filteredCategories.map((category, index) => (
              <CategoryListItem key={category.id} category={category} index={index} />
            ))
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-12"
            >
              <div className="w-24 h-24 bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
                <Folder size={32} className="text-slate-400" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">
                {searchTerm ? 'No categories found' : 'No categories yet'}
              </h3>
              <p className="text-slate-400 mb-6">
                {searchTerm 
                  ? 'Try adjusting your search terms' 
                  : 'Categories will appear here when added'
                }
              </p>
              {!searchTerm && (
                <motion.button
                  onClick={handleManageCategories}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-teal-500 hover:bg-teal-600 text-white px-6 py-3 rounded-lg transition-colors duration-200"
                >
                  Add First Category
                </motion.button>
              )}
            </motion.div>
          )}
        </div>
      </div>

      <BottomNavigation />
    </div>
  );
};

export default AdminCategoryList;
